import { useCallback, useContext, useEffect, useRef, useState, type MouseEvent } from 'react'

import { gnarSpec } from '@lib/tracking/gnar'
import { waitGnar } from '@lib/utils/promise'

import { PageContext } from '@contexts/page-context'

type Placement = Parameters<typeof gnarSpec.ctaButtonClick>[0]['metadata']['placement']
type StyleVariant = Parameters<typeof gnarSpec.ctaButtonClick>[0]['metadata']['styleVariant']

export interface UseCtaTrackingOptions {
  placement: Placement
  openInNewTab?: boolean
  loadingTimeout?: number
}

export function useCtaTracking({
  placement,
  openInNewTab = false,
  loadingTimeout = 3000,
}: UseCtaTrackingOptions) {
  const { pageId, slug } = useContext(PageContext)
  const [isLoading, setIsLoading] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const trackAndNavigate = useCallback(
    async (
      ctaType: string,
      ctaLabel: string,
      category: StyleVariant,
      url?: string,
      event?: MouseEvent<HTMLAnchorElement> | MouseEvent<HTMLButtonElement>,
    ) => {
      // Prevent multiple clicks while loading
      if (isLoading) {
        event?.preventDefault()
        return
      }

      setIsLoading(true)

      // Prevent default behavior for anchor tags
      // Use currentTarget (element with handler) not target (clicked element)
      if (event?.currentTarget instanceof HTMLAnchorElement) {
        event.preventDefault()
      }

      // Attempt tracking but don't let it block navigation
      try {
        await waitGnar(
          gnarSpec.ctaButtonClick({
            ctaType,
            displayContent: ctaLabel,
            metadata: { placement, styleVariant: category },
            pageId,
            pageSlug: slug,
            targetUrl: url,
          }),
        )
      } catch (error) {
        // Log the error but continue with navigation
        console.warn('CTA tracking failed:', error)
      }

      // Always navigate regardless of tracking success/failure
      window.open(url, openInNewTab ? '_blank' : '_self')

      // Set a timeout in case the navigation did not happen in which case we want to re-enable the button
      timeoutRef.current = setTimeout(() => {
        setIsLoading(false)
      }, loadingTimeout)
    },
    [pageId, slug, placement, openInNewTab, loadingTimeout, isLoading],
  )

  return { trackAndNavigate, isLoading }
}
